// src/components/reports/SolicitudPDFGenerator.js
import jsPDF from "jspdf";

/**
 * Genera el PDF con el estilo EXACTO que pediste.
 * @param {Object} solicitud   Objeto del crédito/solicitud
 * @param {Object} params
 * @param {Array}  params.comentarios  Array de { fecha, texto, archivos? } o []
 * @param {string} params.logo         DataURL/PNG (si falla, lo ignora)
 */
export function generarCreditoPDF(solicitud, { comentarios = [], logo } = {}) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  let yPosition = 30;

  // Colores corporativos
  const primaryColor = [0, 50, 38];      // #003226
  const secondaryColor = [219, 197, 168]; // #DBC5A8
  const grayColor = [107, 114, 128];
  const lightGray = [243, 244, 246];

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));

  const formatDate = (dateString) => {
    try {
      const d = new Date(dateString);
      return d.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  const checkNewPage = (neededSpace = 20) => {
    if (yPosition + neededSpace > pageHeight - 30) {
      doc.addPage();
      yPosition = 30;
      return true;
    }
    return false;
  };

  // Header con color corporativo
  doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  doc.rect(0, 0, pageWidth, 45, "F");

  const leftMargin = 20;
  const logoWidth = 50;
  const logoHeight = 20;

  // Logo en la parte superior izquierda (si hay)
  if (logo) {
    try {
      doc.addImage(logo, "PNG", leftMargin, 8, logoWidth, logoHeight);
    } catch (e) {
      // Si el logo no está en DataURL base64, jsPDF puede fallar. Ignoramos y seguimos.
      // Para asegurar el render del logo, pasalo como DataURL base64.
    }
  }

  // Línea separadora debajo del logo
  doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
  doc.setLineWidth(0.5);
  doc.line(leftMargin, 8 + logoHeight + 2, leftMargin + logoWidth, 8 + logoHeight + 2);

  // Texto debajo de la línea
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont(undefined, "normal");
  doc.text("Reporte de Solicitud de Crédito", leftMargin, 8 + logoHeight + 10);

  // Fecha del reporte a la derecha
  const fechaReporte = new Date().toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  doc.setFontSize(10);
  doc.text(`Generado: ${fechaReporte}`, pageWidth - 60, 38);

  yPosition = 60;

  // Badge de estado
  const getEstadoColor = (estado) => {
    switch ((estado || "").toLowerCase()) {
      case "aprobada":
        return [16, 185, 129];
      case "rechazada":
        return [239, 68, 68];
      case "revision":
        return [245, 158, 11];
      case "faltapago":
      case "falta de pago":
        return [245, 158, 11];
      default:
        return [59, 130, 246];
    }
  };
  const estadoColor = getEstadoColor(solicitud.estado);
  doc.setFillColor(estadoColor[0], estadoColor[1], estadoColor[2]);
  doc.roundedRect(pageWidth - 80, yPosition - 10, 60, 15, 3, 3, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  doc.text(String(solicitud.estado || "").toUpperCase(), pageWidth - 75, yPosition);

  // Resetear color de texto
  doc.setTextColor(0, 0, 0);
  yPosition += 20;

  // Helper para secciones
  const createSection = (title, content, bgColor = lightGray) => {
    checkNewPage(40);
    doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
    doc.rect(20, yPosition - 8, pageWidth - 40, 20, "F");
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text(title, 25, yPosition + 5);
    yPosition += 25;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont(undefined, "normal");
    content.forEach((item) => {
      checkNewPage();
      if (item.label && item.value !== undefined) {
        doc.setFont(undefined, "bold");
        doc.text(`${item.label}:`, 25, yPosition);
        doc.setFont(undefined, "normal");
        doc.text(String(item.value), 80, yPosition);
      } else if (item.text) {
        const lines = doc.splitTextToSize(item.text, pageWidth - 50);
        lines.forEach((line) => {
          checkNewPage();
          doc.text(line, 25, yPosition);
          yPosition += 6;
        });
        return;
      }
      yPosition += 8;
    });
    yPosition += 10;
  };

  // ID prominente
  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  doc.text(`ID: ${solicitud.id}`, 20, yPosition);
  yPosition += 20;

  // Información Personal
  createSection(" INFORMACIÓN PERSONAL", [
    { label: "Nombre Completo", value: solicitud.nombre || "-" },
    { label: "DNI", value: solicitud.dni || "-" },
    { label: "Email", value: solicitud.email || "-" },
    { label: "Teléfono", value: solicitud.telefono || "-" },
    { label: "Ingresos Mensuales", value: formatCurrency(solicitud.ingresos) },
  ]);

  // Información del Crédito
  const creditoInfo = [
    {
      label: "Tipo de Crédito",
      value:
        (solicitud.tipo || "")
          .charAt(0)
          .toUpperCase() + (solicitud.tipo || "").slice(1),
    },
    { label: "Monto Solicitado", value: formatCurrency(solicitud.montoSolicitado) },
    {
      label: "Plazo",
      value: `${solicitud.plazo || 0} ${
        (solicitud.tipo || "").toLowerCase() === "hipotecario" ? "años" : "meses"
      }`,
    },
    { label: "Fecha de Solicitud", value: formatDate(solicitud.fechaCreacion) },
  ];
  if (solicitud.cuotaCalculada) {
    creditoInfo.push(
      { label: "Cuota Calculada", value: formatCurrency(solicitud.cuotaCalculada) },
      { label: "Total a Pagar", value: formatCurrency(solicitud.totalAPagar || 0) },
      { label: "Tasa Anual", value: `${solicitud.tasaAnual || "-"}%` },
    );
  }
  createSection("DETALLES DEL CRÉDITO", creditoInfo);

  // Observaciones
  if (solicitud.observaciones && String(solicitud.observaciones).trim()) {
    createSection(" OBSERVACIONES", [{ text: String(solicitud.observaciones) }]);
  }

  // Confirm para incluir comentarios internos
  let incluirComentarios = false;
  try {
    incluirComentarios =
      comentarios && comentarios.length > 0
        ? window.confirm(
            `¿Desea incluir los comentarios internos en el PDF?\n\nSe encontraron ${comentarios.length} comentario(s) interno(s).`,
          )
        : false;
  } catch {
    incluirComentarios = false;
  }

  if (incluirComentarios && comentarios.length > 0) {
    createSection(" HISTORIAL DE COMENTARIOS", []);
    comentarios.forEach((comentario, index) => {
      checkNewPage(30);
      doc.setFillColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.circle(30, yPosition, 8, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text(String(index + 1), 27, yPosition + 3);

      doc.setTextColor(grayColor[0], grayColor[1], grayColor[2]);
      doc.setFontSize(9);
      doc.setFont(undefined, "normal");
      doc.text(formatDate(comentario.fecha || new Date()), 45, yPosition + 3);

      yPosition += 15;

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      const cuerpo = String(comentario.texto || comentario.comentario || "");
      const comentarioLines = doc.splitTextToSize(cuerpo, pageWidth - 60);
      comentarioLines.forEach((line) => {
        checkNewPage();
        doc.text(line, 25, yPosition);
        yPosition += 5;
      });

      if (Array.isArray(comentario.archivos) && comentario.archivos.length > 0) {
        yPosition += 5;
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
        doc.setFontSize(9);
        doc.text(`📎 ${comentario.archivos.length} archivo(s) adjunto(s)`, 25, yPosition);
      }
      yPosition += 15;
    });
  }

  // Footer en todas las páginas
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.setLineWidth(0.5);
    doc.line(20, pageHeight - 25, pageWidth - 20, pageHeight - 25);
    doc.setTextColor(grayColor[0], grayColor[1], grayColor[2]);
    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.text("Financiera Gamaan - Reporte Confidencial", 20, pageHeight - 15);
    doc.text(`Página ${i} de ${totalPages}`, pageWidth - 40, pageHeight - 15);
  }

  // Guardar PDF
  const tipoCredito =
    (solicitud.tipo || "").charAt(0).toUpperCase() + (solicitud.tipo || "").slice(1);
  const fechaCorta = new Date().toISOString().split("T")[0];
  doc.save(
    `Solicitud_${tipoCredito}_${String(solicitud.id || "").substring(0, 8)}_${fechaCorta}.pdf`,
  );
}
